import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import axios from 'axios'
import {
  Button,
  ButtonColor,
  Loader,
  NotificationType,
  PortfolioGrid,
  VideoUpload,
} from '../../../../components'
import {
  GetPortfolioByCreatorResponse,
  GET_PORTFOLIO_BY_CREATOR,
  GET_PORTFOLIO_ITEM_UPLOAD_URL,
  CREATE_PORTFOLIO_ITEM,
} from '../../../../gql'
import {
  Creator,
  GetPortfolioSort,
  GetUploadUrlResponse,
  MutationCreatePortfolioItemArgs,
  MutationGetPortfolioItemUploadUrlArgs,
  PortfolioItem,
  QueryGetPortfolioByCreatorArgs,
} from '../../../../gql/types'
import { useNotification, useObjectUrl } from '../../../../utils/hooks'

interface Props {
  creator: Creator
  onContinue: () => void
  onBack: () => void
}

const ReapplyPortfolio = ({ creator, onContinue, onBack }: Props) => {
  const { addNotification } = useNotification()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [mime, setMime] = useState('')
  const [contentDimensions, setContentDimensions] = useState<{
    w: number | null
    h: number | null
  }>({ w: null, h: null })
  const [uploadProgress, setUploadProgress] = useState(0)
  const previewUrl = useObjectUrl(selectedFile)

  const [getPortfolioItemUploadUrl] = useMutation<
    { getPortfolioItemUploadUrl: GetUploadUrlResponse },
    MutationGetPortfolioItemUploadUrlArgs
  >(GET_PORTFOLIO_ITEM_UPLOAD_URL)

  const [createPortfolioItem] = useMutation<
    { createPortfolioItem: PortfolioItem },
    MutationCreatePortfolioItemArgs
  >(CREATE_PORTFOLIO_ITEM, {
    refetchQueries: [
      {
        query: GET_PORTFOLIO_BY_CREATOR,
        variables: {
          creatorId: creator.id,
          options: {
            skip: 0,
            limit: 12,
            sort: GetPortfolioSort.newest,
          },
        },
      },
    ],
  })

  const {
    loading: getPortfolioLoading,
    error: getPortfolioError,
    data: getPortfolioData,
  } = useQuery<GetPortfolioByCreatorResponse, QueryGetPortfolioByCreatorArgs>(
    GET_PORTFOLIO_BY_CREATOR,
    {
      variables: {
        creatorId: creator.id,
        options: {
          skip: 0,
          limit: 12,
          sort: GetPortfolioSort.newest,
        },
      },
    },
  )

  const reset = () => {
    setSelectedFile(null)
    setMime('')
    setUploadProgress(0)
  }

  const uploadContent = async (
    file: File,
    mimeType: string,
    dimensions: { w: number | null; h: number | null },
  ) => {
    setError(null)
    setLoading(true)

    try {
      const { data } = await getPortfolioItemUploadUrl({
        variables: {
          creatorId: creator.id,
          mime: mimeType,
        },
      })

      await axios.put(data.getPortfolioItemUploadUrl.uploadUrl, file, {
        headers: {
          'Content-Type': mimeType,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(percentCompleted)
        },
      })

      await createPortfolioItem({
        variables: {
          creatorId: creator.id,
          input: {
            videoFileName: data.getPortfolioItemUploadUrl.fileName,
            videoWidth: dimensions.w,
            videoHeight: dimensions.h,
          },
        },
      })

      addNotification('Portfolio video uploaded', NotificationType.success)
      reset()
    } catch (error) {
      console.log('Error Creating portfolio video', error?.message)
      addNotification(
        'Something went wrong. Please try again.',
        NotificationType.error,
      )
    }

    setLoading(false)
  }

  const handleFileInput = (
    file: File,
    size?: {
      w: number | null
      h: number | null
    },
  ) => {
    if (!file) {
      setSelectedFile(null)
      setMime('')
      return
    }

    setSelectedFile(file)
    setMime(file.type)
    setError(null)
    setContentDimensions(size)

    uploadContent(file, file.type, size)
  }

  if (error || getPortfolioError) {
    console.log('getPortfolioError', getPortfolioError?.message)
    console.log('error', error)
  }

  const portfolioItemsCount =
    getPortfolioData?.getPortfolioByCreator?.items?.length ?? 0

  const handleContinue = (event: React.MouseEvent) => {
    event.preventDefault()

    if (portfolioItemsCount < creator.minPortfolioItems) {
      setError(`Please upload at least ${creator.minPortfolioItems} videos.`)
      return
    }

    onContinue()
  }

  return (
    <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8'>
      <div className='py-5 md:bg-white space-y-6 sm:p-6'>
        <div className='sm:mx-auto'>
          <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
            Update Portfolio
          </h2>
          <div className='mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-md'>
            <p className='text-sm text-blue-900 font-semibold'>
              💡 These videos will be displayed to brands and are the most
              important part of your application.
            </p>
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            Upload at least three of your <strong>absolute best</strong> UGC
            videos from previous brand collaborations. Your portfolio should
            showcase different styles of UGC (unboxing, testimonial, how-to).
            Videos should be between 15 seconds to 1 minute.
          </p>
          <div className='mt-4 p-3 bg-gray-50 border border-gray-300 rounded-md'>
            <p className='text-sm text-gray-700 font-medium mb-2'>Required:</p>
            <ul className='text-sm text-gray-700 list-disc list-inside space-y-1'>
              <li>Videos must be in English</li>
              <li>
                Must include talking to the camera/voiceover (silent videos are
                not accepted)
              </li>
              <li>
                No AI voiceovers or AI-generated content (must be authentic,
                human-created content)
              </li>
            </ul>
          </div>
          <div className='mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-md'>
            <p className='text-sm text-red-900 font-semibold mb-2'>
              ⚠️ WARNING: Your application will be rejected if you:
            </p>
            <ul className='text-sm text-red-800 list-disc list-inside space-y-1'>
              <li>
                Submit videos that aren't UGC (User-Generated Content) you
                created for brands.
              </li>
              <li>
                Upload any other content types (vlogs, lifestyle content, etc.).
              </li>
            </ul>
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            <a
              href='https://useclip.com/ugc-content'
              className='text-blue-600 font-medium hover:text-blue-700'
              target='_blank'
              rel='noopener noreferrer'
            >
              See examples
            </a>{' '}
            of approved videos by other creators on Clip.
          </p>
        </div>

        <div className='mt-6 w-full'>
          {loading && previewUrl && (
            <div className='flex flex-col justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6'>
              <div className='mb-4 flex mx-auto'>
                <video className='max-h-[350px]' src={previewUrl} controls />
              </div>
            </div>
          )}
          {!loading && <VideoUpload onChange={handleFileInput} />}
          {error && <p className='text-red-500 text-sm mt-2'>{error}</p>}
        </div>

        {getPortfolioLoading && <Loader />}
        {!getPortfolioLoading && getPortfolioData && (
          <div className='-mx-4'>
            <PortfolioGrid
              items={getPortfolioData.getPortfolioByCreator.items}
              isFullScreen={true}
              isDeleteAvailable={true}
            />
          </div>
        )}
      </div>

      <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
        <Button
          title='Go back'
          type='button'
          colour={ButtonColor.white}
          loading={loading}
          disabled={loading}
          onClick={onBack}
        />
        <Button
          title={
            loading && uploadProgress > 0
              ? `Uploading... ${uploadProgress}%`
              : 'Continue'
          }
          type='button'
          loading={!(loading && uploadProgress > 0) ? loading : undefined}
          disabled={loading}
          loadingPercentage={loading ? uploadProgress : undefined}
          onClick={handleContinue}
          width='160px'
        />
      </div>
    </div>
  )
}

export { ReapplyPortfolio }
